<script setup>
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'view',
    subject: 'MasterData',
  },
})

const ability = useAbility()

const items = ref([])
const loading = ref(false)
const totalItems = ref(0)
const page = ref(1)
const perPage = ref(15)
const search = ref('')

const dialog = ref(false)
const dialogTitle = ref('')
const editingId = ref(null)
const saving = ref(false)
const errors = ref({})

const formDefault = () => ({
  vendor_id: null, title: '', type: 'kontrak', document_number: '',
  start_date: '', end_date: '', status: 'active', is_active: true,
})

const form = ref(formDefault())

const typeOptions = [
  { title: 'PKS', value: 'pks' },
  { title: 'MOU', value: 'mou' },
  { title: 'Kontrak', value: 'kontrak' },
  { title: 'Lainnya', value: 'lainnya' },
]

const statusOptions = [
  { title: 'Aktif', value: 'active' },
  { title: 'Kadaluarsa', value: 'expired' },
  { title: 'Diakhiri', value: 'terminated' },
]

const deleteDialog = ref(false)
const deletingItem = ref(null)
const deleting = ref(false)

const fetchItems = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (search.value) query.search = search.value

    const response = await useApi('/api/v1/master-data/contracts', { query }).get()
    const data = response?.data ?? response

    items.value = Array.isArray(data) ? data : (data?.data ?? [])
    totalItems.value = data?.meta?.total ?? data?.total ?? 0
  } catch (e) {
    console.error('Fetch contracts error:', e)
  } finally {
    loading.value = false
  }
}

onMounted(fetchItems)
watch(search, () => { page.value = 1; fetchItems() })
watch(page, fetchItems)

const openCreate = () => {
  editingId.value = null
  form.value = formDefault()
  errors.value = {}
  dialogTitle.value = 'Tambah Kontrak'
  dialog.value = true
}

const openEdit = item => {
  editingId.value = item.id
  form.value = {
    vendor_id: item.vendor_id,
    title: item.title,
    type: item.type,
    document_number: item.document_number ?? '',
    start_date: item.start_date ?? '',
    end_date: item.end_date ?? '',
    status: item.status,
    is_active: item.is_active,
  }
  errors.value = {}
  dialogTitle.value = 'Edit Kontrak'
  dialog.value = true
}

const save = async () => {
  saving.value = true
  errors.value = {}
  try {
    const api = useApi(editingId.value ? `/api/v1/master-data/contracts/${editingId.value}` : '/api/v1/master-data/contracts')
    const response = editingId.value ? await api.put({ body: form.value }) : await api.post({ body: form.value })
    const saved = response?.data ?? response

    if (editingId.value) {
      const idx = items.value.findIndex(i => i.id === editingId.value)
      if (idx !== -1) items.value[idx] = saved
    } else {
      fetchItems()
    }
    dialog.value = false
  } catch (e) {
    if (e?.status === 422) errors.value = e?.data?.errors ?? {}
    else console.error('Save contract error:', e)
  } finally {
    saving.value = false
  }
}

const confirmDelete = item => { deletingItem.value = item; deleteDialog.value = true }

const doDelete = async () => {
  deleting.value = true
  try {
    await useApi(`/api/v1/master-data/contracts/${deletingItem.value.id}`).delete()
    deleteDialog.value = false
    fetchItems()
  } catch (e) {
    console.error('Delete contract error:', e)
  } finally {
    deleting.value = false
  }
}

const statusColor = value => ({ active: 'success', expired: 'warning', terminated: 'error' }[value] ?? 'secondary')
const statusLabel = value => statusOptions.find(s => s.value === value)?.title ?? value

const headers = [
  { title: 'Judul', key: 'title' },
  { title: 'Vendor', key: 'vendor' },
  { title: 'Jenis', key: 'type' },
  { title: 'Mulai', key: 'start_date' },
  { title: 'Berakhir', key: 'end_date' },
  { title: 'Status', key: 'status', align: 'center', width: 120 },
  { title: 'Aksi', key: 'actions', align: 'center', sortable: false, width: 120 },
]
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h4 font-weight-bold">
              Kontrak
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Master data PKS/MOU/kontrak dengan vendor
            </p>
          </div>
          <VBtn
            v-if="ability.can('manage', 'MasterData')"
            prepend-icon="ri-add-line"
            @click="openCreate"
          >
            Tambah Kontrak
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <VCard class="mb-4">
      <VCardText>
        <VTextField
          v-model="search"
          placeholder="Cari judul atau nomor dokumen..."
          prepend-inner-icon="ri-search-line"
          clearable
          hide-details
          density="compact"
          style="max-width: 320px;"
        />
      </VCardText>
    </VCard>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="perPage"
        hide-default-footer
      >
        <template #item.vendor="{ item }">
          {{ item.vendor?.name ?? '-' }}
        </template>
        <template #item.type="{ item }">
          {{ typeOptions.find(t => t.value === item.type)?.title ?? item.type }}
        </template>
        <template #item.status="{ item }">
          <VChip
            size="small"
            :color="statusColor(item.status)"
          >
            {{ statusLabel(item.status) }}
          </VChip>
        </template>
        <template #item.actions="{ item }">
          <VBtn
            v-if="ability.can('manage', 'MasterData')"
            icon="ri-pencil-line"
            variant="text"
            size="small"
            color="primary"
            @click="openEdit(item)"
          />
          <VBtn
            v-if="ability.can('manage', 'MasterData')"
            icon="ri-delete-bin-line"
            variant="text"
            size="small"
            color="error"
            @click="confirmDelete(item)"
          />
        </template>
        <template #no-data>
          <div class="text-center py-10">
            <p class="text-medium-emphasis">
              Belum ada data
            </p>
          </div>
        </template>
      </VDataTable>
      <VDivider />
      <VCardText class="d-flex align-center justify-space-between flex-wrap gap-3 pa-4">
        <span class="text-body-2 text-medium-emphasis">Total: {{ totalItems }}</span>
        <VPagination
          v-if="totalItems > perPage"
          v-model="page"
          :length="Math.ceil(totalItems / perPage)"
          :total-visible="5"
          density="compact"
        />
      </VCardText>
    </VCard>

    <VDialog
      v-model="dialog"
      max-width="640"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-0">
          {{ dialogTitle }}
        </VCardTitle>
        <VCardText class="pa-6">
          <VRow>
            <VCol cols="12">
              <MasterDataPicker
                v-model="form.vendor_id"
                entity="vendors"
                label="Vendor *"
                :error-messages="errors.vendor_id"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="form.title"
                label="Judul Kontrak *"
                :error-messages="errors.title"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VSelect
                v-model="form.type"
                :items="typeOptions"
                label="Jenis *"
                :error-messages="errors.type"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VTextField
                v-model="form.document_number"
                label="Nomor Dokumen"
                :error-messages="errors.document_number"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VTextField
                v-model="form.start_date"
                label="Tanggal Mulai *"
                type="date"
                :error-messages="errors.start_date"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VTextField
                v-model="form.end_date"
                label="Tanggal Berakhir"
                type="date"
                :error-messages="errors.end_date"
              />
            </VCol>
            <VCol cols="12">
              <VSelect
                v-model="form.status"
                :items="statusOptions"
                label="Status *"
                :error-messages="errors.status"
              />
            </VCol>
            <VCol cols="12">
              <VSwitch
                v-model="form.is_active"
                label="Aktif"
                color="success"
                hide-details
              />
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="saving"
            @click="dialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="save"
          >
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="deleteDialog"
      max-width="420"
    >
      <VCard>
        <VCardTitle class="pa-6 pb-2">
          Hapus Kontrak?
        </VCardTitle>
        <VCardText class="pa-6 pt-0">
          <strong>{{ deletingItem?.title }}</strong> akan dihapus.
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="deleting"
            @click="deleteDialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="error"
            :loading="deleting"
            @click="doDelete"
          >
            Hapus
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
